/**
 * GameClient — typed PartySocket wrapper for multiplayer communication.
 *
 * Handles connection to the PartyKit game-room server with:
 * - Typed send/receive for ClientMessage/ServerMessage
 * - Event listener registration by message type
 * - Auto-reconnection (built into PartySocket)
 */

import PartySocket from 'partysocket';
import type { ClientMessage, ServerMessage } from './messages';

type MessageHandler<T extends ServerMessage['type']> = (
    msg: Extract<ServerMessage, { type: T }>,
) => void;

export class GameClient {
    private socket: PartySocket | null = null;
    private handlers = new Map<string, Set<(msg: ServerMessage) => void>>();
    private _connected = false;

    get connected(): boolean {
        return this._connected;
    }

    /**
     * Connect to a PartyKit game room.
     * @param host - PartyKit host (e.g. "localhost:1999" for dev, or deployed URL)
     * @param roomId - Room identifier (room code from lobby)
     */
    connect(host: string, roomId: string): void {
        this.disconnect();

        this.socket = new PartySocket({
            host,
            room: roomId.toLowerCase(),
        });

        this.socket.addEventListener('open', () => {
            this._connected = true;
            if (import.meta.env.DEV) console.log(`[NET] Connected to room: ${roomId}`);
        });

        this.socket.addEventListener('message', (event: MessageEvent) => {
            try {
                const msg = JSON.parse(event.data as string) as ServerMessage;
                this.dispatch(msg);
            } catch (e) {
                console.warn('[NET] Failed to parse message:', e);
            }
        });

        this.socket.addEventListener('close', (event: CloseEvent) => {
            this._connected = false;
            if (import.meta.env.DEV) console.log(`[NET] Disconnected — code: ${event.code}, reason: "${event.reason}", clean: ${event.wasClean}`);
        });

        this.socket.addEventListener('error', (event: Event) => {
            console.error('[NET] Socket error:', event);
        });
    }

    /** Send a typed message to the server. */
    send(msg: ClientMessage): void {
        if (this.socket && this._connected) {
            const json = JSON.stringify(msg);
            if (json.length > 50000) {
                console.warn(`[NET] Large message: type=${msg.type}, size=${(json.length / 1024).toFixed(1)}KB`);
            }
            this.socket.send(json);
        }
    }

    /** Register a handler for a specific server message type. */
    on<T extends ServerMessage['type']>(
        type: T,
        handler: MessageHandler<T>,
    ): () => void {
        if (!this.handlers.has(type)) {
            this.handlers.set(type, new Set());
        }
        const typedHandler = handler as (msg: ServerMessage) => void;
        this.handlers.get(type)!.add(typedHandler);

        // Return unsubscribe function
        return () => {
            this.handlers.get(type)?.delete(typedHandler);
        };
    }

    /** Disconnect and clean up. Does NOT clear handlers — they persist across reconnections. */
    disconnect(): void {
        if (this.socket) {
            this.socket.close();
            this.socket = null;
            this._connected = false;
        }
    }

    /** Full cleanup — clears handlers too. Call only on final teardown. */
    destroy(): void {
        this.disconnect();
        this.handlers.clear();
    }

    private dispatch(msg: ServerMessage): void {
        // Only log non-spammy messages (skip key_state, aim_update which fire every frame)
        if (msg.type !== 'key_state' && msg.type !== 'aim_update') {
            if (import.meta.env.DEV) console.log('[NET] Dispatch:', msg.type, msg);
        }
        const typeHandlers = this.handlers.get(msg.type);
        if (typeHandlers) {
            for (const handler of typeHandlers) {
                handler(msg);
            }
        }

        // Also dispatch to wildcard '*' handlers
        const wildcardHandlers = this.handlers.get('*');
        if (wildcardHandlers) {
            for (const handler of wildcardHandlers) {
                handler(msg);
            }
        }
    }
}
